import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut } from 'lucide-react';
import { useAuthStore } from '@/app/auth/stores/auth.store';
import { Button } from '@/shared/components/Button';
import { cn } from '@/shared/lib/cn';

const getInitials = (nameOrEmail: string) =>
  nameOrEmail
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

interface ProfileCardProps {
  collapsed: boolean;
}

export const ProfileCard = ({ collapsed }: ProfileCardProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('app');
  const session = useAuthStore((s) => s.session);
  const clearSession = useAuthStore((s) => s.clearSession);
  const profile = session?.user
    ? { displayName: session.user.display_name, email: session.user.email }
    : null;
  const userName = profile?.displayName ?? profile?.email ?? t('profile.unknownUser');

  const handleLogout = () => {
    clearSession();
    void navigate('/login', { replace: true });
  };

  return (
    <section
      aria-label={t('profile.aria')}
      className={cn('flex flex-col', collapsed ? 'items-center gap-1' : 'gap-2')}
    >
      <button
        className={cn(
          'flex min-w-0 w-full items-center text-left cursor-pointer',
          collapsed ? '' : 'gap-2',
        )}
        onClick={() => {
          void navigate('/account');
        }}
        title={t('profile.openAccount')}
        type="button"
      >
        <span
          className="grid place-items-center rounded-sm font-extrabold w-8 h-8 text-white bg-primary shrink-0"
          title={userName}
        >
          {getInitials(userName) || 'U'}
        </span>
        <span
          className={cn(
            'min-w-0 flex-1 overflow-hidden',
            collapsed ? 'max-w-0 opacity-0' : 'max-w-xs',
          )}
        >
          <span className="block truncate text-sm font-extrabold text-ink">{userName}</span>
        </span>
      </button>
      {collapsed ? (
        <Button
          aria-label={t('profile.logout')}
          iconOnly
          leadingIcon={<LogOut aria-hidden="true" className="size-4" />}
          onClick={handleLogout}
          size="sm"
          title={t('profile.logout')}
          type="button"
          variant="ghost"
        />
      ) : (
        <Button fullWidth onClick={handleLogout} size="sm" type="button" variant="ghost">
          {t('profile.logout')}
        </Button>
      )}
    </section>
  );
};
